import { useState, useEffect, useCallback } from 'react';
import {
  ALL_ARCHIVE_SEARCH_FIELDS,
  normalizeArchiveSearchFields,
} from '@/constants/archiveSearchFields';
import type { ColumnMatchMode, VorgangFilters } from '@/types/filters';
import {
  changeArchiveRange,
  recentArchiveRange,
  restoreArchiveWindow,
  type ArchiveWindow,
} from './archiveWindow';

const storageKeyFor = (scope: 'main' | 'archiv') =>
  scope === 'archiv' ? 'vorgang-filters-archiv' : 'vorgang-filters';

const normalizeColumnMatchMode = (value: unknown): ColumnMatchMode =>
  value === 'equals' ? 'equals' : 'contains';

export const defaultFilters: VorgangFilters = {
  auftragsnummerOperator: 'contains',
  vertragsnummerOperator: 'contains',
  externerNameOperator: 'contains',
  dokumentTypOperator: 'contains',
  status: '',
  archivierungsstatus: '',
  createdAt: { from: undefined, to: undefined },
  auftragsnummer: '',
  vertragsnummer: '',
  kundennummer: '',
  correlationId: '',
  externeId: '',
  externerName: '',
  vorgangType: '',
  error: '',
  lockedBy: '',
  currentGestartedAm: { from: undefined, to: undefined },
  currentStep: '',
  fehlertyp: '',
  dokumentTyp: '',
  unterdrueckung: '',
  searchText: '',
  searchOperator: 'contains',
  stale24h: false,
};

export const useVorgangFilters = (scope: 'main' | 'archiv' = 'main') => {
  const isArchive = scope === 'archiv';
  const legacyStorageKey = storageKeyFor(scope);
  const storageKey = isArchive
    ? `${legacyStorageKey}-core-7245`
    : legacyStorageKey;

  const makeState = (filters: VorgangFilters, storedWindow?: unknown) => {
    if (scope !== 'archiv')
      return { filters, archiveWindow: 'recent' as ArchiveWindow };
    const modes = {
      auftragsnummerOperator: normalizeColumnMatchMode(
        filters.auftragsnummerOperator,
      ),
      vertragsnummerOperator: normalizeColumnMatchMode(
        filters.vertragsnummerOperator,
      ),
      externerNameOperator: normalizeColumnMatchMode(
        filters.externerNameOperator,
      ),
      dokumentTypOperator: normalizeColumnMatchMode(
        filters.dokumentTypOperator,
      ),
    };
    const archiveState = restoreArchiveWindow(filters.createdAt, storedWindow);
    return {
      filters: {
        ...filters,
        ...modes,
        searchFields: normalizeArchiveSearchFields(filters.searchFields),
        createdAt: archiveState.createdAt,
      },
      archiveWindow: archiveState.archiveWindow,
    };
  };
  const [state, setState] = useState(() => {
    // Initialize from localStorage if available
    if (typeof window === 'undefined') return makeState(defaultFilters);

    try {
      const stored =
        localStorage.getItem(storageKey) ??
        (isArchive ? localStorage.getItem(legacyStorageKey) : null);
      if (stored) {
        const { archiveWindow, ...parsedFilters } = JSON.parse(stored);

        // Ensure createdAt has the proper structure
        parsedFilters.createdAt = {
          from: parsedFilters.createdAt?.from
            ? new Date(parsedFilters.createdAt.from)
            : undefined,
          to: parsedFilters.createdAt?.to
            ? new Date(parsedFilters.createdAt.to)
            : undefined,
        };

        // Ensure currentGestartedAm has the proper structure
        parsedFilters.currentGestartedAm = {
          from: parsedFilters.currentGestartedAm?.from
            ? new Date(parsedFilters.currentGestartedAm.from)
            : undefined,
          to: parsedFilters.currentGestartedAm?.to
            ? new Date(parsedFilters.currentGestartedAm.to)
            : undefined,
        };

        // Merge with default filters to ensure all properties exist
        const mergedFilters = { ...defaultFilters, ...parsedFilters };
        return makeState(mergedFilters, archiveWindow);
      }
    } catch {
      // Silently fail if localStorage is not available or corrupted
    }

    return makeState(defaultFilters);
  });
  const { filters, archiveWindow } = state;

  // Save filters to localStorage whenever they change
  useEffect(() => {
    if (typeof window === 'undefined') return;

    try {
      localStorage.setItem(
        storageKey,
        JSON.stringify({
          ...filters,
          ...(isArchive ? { archiveWindow } : {}),
        }),
      );
    } catch {
      // Silently fail if localStorage is not available
    }
  }, [filters, storageKey, isArchive, archiveWindow]);

  const updateFilters = useCallback(
    (newFilters: Partial<VorgangFilters>) => {
      setState((prev) => {
        const range =
          isArchive && newFilters.createdAt
            ? changeArchiveRange(newFilters.createdAt)
            : undefined;
        return {
          filters: {
            ...prev.filters,
            ...newFilters,
            ...(range ? { createdAt: range.createdAt } : {}),
          },
          archiveWindow: range?.archiveWindow ?? prev.archiveWindow,
        };
      });
    },
    [isArchive],
  );

  const setArchiveWindow = useCallback(
    (next: 'recent' | 'all') => {
      if (!isArchive) return;
      setState((prev) => ({
        filters: {
          ...prev.filters,
          createdAt:
            next === 'recent'
              ? recentArchiveRange()
              : { from: undefined, to: undefined },
        },
        archiveWindow: next,
      }));
    },
    [isArchive],
  );

  const clearFilters = useCallback(() => {
    setState({
      filters: isArchive
        ? {
            ...defaultFilters,
            searchFields: [...ALL_ARCHIVE_SEARCH_FIELDS],
            createdAt: recentArchiveRange(),
          }
        : defaultFilters,
      archiveWindow: isArchive ? 'recent' : 'all',
    });
  }, [isArchive]);

  const clearLocalStorage = useCallback(() => {
    localStorage.removeItem(storageKey);
    clearFilters();
  }, [storageKey, clearFilters]);

  const hasActiveFilters = useCallback(() => {
    return (
      !!filters.archivierungsstatus ||
      filters.status !== '' ||
      (filters.createdAt &&
        (filters.createdAt.from !== undefined ||
          filters.createdAt.to !== undefined)) ||
      filters.auftragsnummer !== '' ||
      filters.vertragsnummer !== '' ||
      filters.kundennummer !== '' ||
      filters.correlationId !== '' ||
      filters.externeId !== '' ||
      filters.externerName !== '' ||
      filters.vorgangType !== '' ||
      filters.error !== '' ||
      filters.lockedBy !== '' ||
      filters.currentStep !== '' ||
      filters.fehlertyp !== '' ||
      filters.dokumentTyp !== '' ||
      filters.unterdrueckung !== '' ||
      filters.searchText !== '' ||
      filters.stale24h ||
      (filters.currentGestartedAm &&
        (filters.currentGestartedAm.from !== undefined ||
          filters.currentGestartedAm.to !== undefined))
    );
  }, [filters]);

  return {
    filters,
    archiveWindow,
    setArchiveWindow,
    updateFilters,
    clearFilters,
    clearLocalStorage,
    hasActiveFilters: hasActiveFilters(),
  };
};

export const useMainVorgangFilters = () => useVorgangFilters('main');

export const useArchivVorgangFilters = () => useVorgangFilters('archiv');
